import { useEffect, useMemo, useState, useCallback } from "react";
import { useQuery, useMutation } from "@apollo/client";
import moment from "moment";
import { EventBus, EVENTS } from "src/services/eventEmitter";

import { getAIChatById, getAIChats } from "../../../../queries/ai.graphql";
import {
  upsertAIChat,
  upsertAIChatMessage,
} from "../../../../queries/mutations/aiChatMutations.graphql";

/**
 * @typedef {import('../../../../components/AIContent/types').Message} Message
 * @typedef {import('../../../../components/AIContent/types').CreateChatMessage} CreateChatMessage
 */

const useGetAIChat = () => {
  const [chatId, setChatId] = useState(null);

  const [currentChat, setCurrentChat] = useState(null);

  useEffect(() => {
    const onDashboardSelected = (id) => {
      setChatId(id);
    };

    EventBus.on(EVENTS.AI_DASHBOARD_SELECTED, onDashboardSelected);

    return () => {
      EventBus.off(EVENTS.AI_DASHBOARD_SELECTED, onDashboardSelected);
    };
  }, []);

  const {
    data: chatsData,
    loading: chatsLoading,
    error: chatsError,
    refetch: refetchChats,
  } = useQuery(getAIChats, {});

  const chats = useMemo(() => chatsData?.chats || [], [chatsData?.chats]);

  const sortedChats = useMemo(() => {
    if (!Array.isArray(chats) || chats.length === 0) {
      return [];
    }
    // Sort by 'createdAt' or 'updatedAt' in descending order (most recent first)
    return [...chats].sort((a, b) => {
      const dateA = moment(parseInt(a.updatedAt || a.createdAt, 10)).diff(
        moment()
      );
      const dateB = moment(parseInt(b.updatedAt || b.createdAt, 10)).diff(
        moment()
      );

      return dateB - dateA;
    });
  }, [chats]);

  const {
    data: currentChatData,
    loading: chatLoading,
    error: chatError,
    updateQuery,
  } = useQuery(getAIChatById, {
    variables: { id: chatId },
    skip: !chatId,
    fetchPolicy: "network-only",
  });

  const [upsertAIChatMutation] = useMutation(upsertAIChat);
  const [upsertAIChatMessageMutation] = useMutation(upsertAIChatMessage);

  const updateChatOptimistically = useCallback((message) => {
    setCurrentChat((prevChat) => {
      const prevMessages = prevChat?.messages || [];
      const hasMessage = prevMessages?.some((m) => m.id === message.id);

      const updatedMessages = hasMessage
        ? prevMessages.map((m) => (m.id === message.id ? message : m))
        : [...prevMessages, message];

      return {
        ...prevChat,
        messages: updatedMessages,
      };
    });
  }, []);

  /**
   * @type {CreateChatMessage}
   */
  const onUpsertAIChatMessage = useCallback(
    async (message) => {
      if (!chatId) {
        return null;
      }

      const result = await upsertAIChatMessageMutation({
        variables: {
          input: {
            ...message,
            aiChatId: chatId,
          },
        },
        optimisticResponse: {
          __typename: "Mutation",
          message: {
            __typename: "AIChatMessage",
            id: message.id,
            aiChatId: chatId,
            role: message.role,
            content: message.content,
            status: message.status,
            citations: message.citations,
            respondsTo: message.respondsTo,
            tools: message.tools,
          },
        },
        update: (proxy, response) => {
          const newMessage = response.data.message;

          updateChatOptimistically(newMessage);
          // Update title of chat if user message is added for updating the title in ChatHistoryPanel.
          if (message.role === "user") {
            updateQuery((prevQuery) => {
              return {
                chat: {
                  ...prevQuery.chat,
                  title: message.content,
                },
              };
            });
          }
        },
      });

      return result;
    },
    [chatId, upsertAIChatMessageMutation, updateChatOptimistically, updateQuery]
  );

  const onStartNewChat = useCallback(async () => {
    setChatId(null);
    setCurrentChat(null);

    const res = await upsertAIChatMutation({
      variables: { input: { id: null } },
    });

    const newChatId = res.data.chat.id;
    setChatId(newChatId);
    refetchChats();

    return newChatId;
  }, [setChatId, setCurrentChat, upsertAIChatMutation, refetchChats]);

  const onSelectChat = useCallback(
    (selectedChatId) => {
      setChatId(selectedChatId);
    },
    [setChatId]
  );

  useEffect(() => {
    if (currentChatData?.chat && currentChatData.chat.id !== currentChat?.id) {
      setCurrentChat(currentChatData.chat);
    }
  }, [currentChatData, currentChat]);

  useEffect(() => {
    if (chatsData && chats.length && !chatId) {
      const defaultChatId = chats[0].id;
      setChatId(defaultChatId);
    }
  }, [chatsData, chats, chatId]);

  useEffect(() => {
    if (!chatsLoading && !chats.length && !chatId) {
      upsertAIChatMutation({
        variables: { input: { id: null } },
      })
        .then((res) => {
          const newChatId = res?.data?.chat?.id;
          setChatId(newChatId);
          refetchChats();
        })
        .catch((err) => {
          // eslint-disable-next-line no-console
          console.error(err);
        });
    }
  }, [chatsLoading, chats, upsertAIChatMutation, chatId, refetchChats]);

  return {
    chatId,
    chats: sortedChats,
    currentChat,
    loading: chatsLoading || chatLoading,
    error: chatsError || chatError,
    onUpsertAIChatMessage,
    updateChatOptimistically,
    onStartNewChat,
    onSelectChat,
  };
};

export default useGetAIChat;
